import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./PaginationControls.css";

export default function PaginationControls({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Hide pagination when there are no items
  if (totalItems === 0) return null;

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7; // Show up to 7 page buttons

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Near the beginning: 1 2 3 4 ... last
        pages.push(2, 3, 4);
        pages.push("ellipsis-end");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end: 1 ... last-3 last-2 last-1 last
        pages.push("ellipsis-start");
        pages.push(totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // In the middle: 1 ... current-1 current current+1 ... last
        pages.push("ellipsis-start");
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push("ellipsis-end");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="pagination-controls">
      <div className="pagination-info">
        <p>
          Showing <span className="pagination-info-number">{startIndex}</span>{" "}
          to <span className="pagination-info-number">{endIndex}</span> of{" "}
          <span className="pagination-info-number">{totalItems}</span> results
        </p>
      </div>

      <nav aria-label="Pagination" className="pagination-nav">
        <button
          className="pagination-nav-button pagination-nav-button--prev"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canGoPrevious}
          aria-label="Previous page"
        >
          <ChevronLeft size={18} />
        </button>

        {pageNumbers.map((page, index) => {
          if (page === "ellipsis-start" || page === "ellipsis-end") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="pagination-nav-ellipsis"
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              className={`pagination-nav-button pagination-nav-button--page ${
                page === currentPage ? "pagination-nav-button--active" : ""
              }`}
              onClick={() => onPageChange(page)}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </button>
          );
        })}

        <button
          className="pagination-nav-button pagination-nav-button--next"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canGoNext}
          aria-label="Next page"
        >
          <ChevronRight size={18} />
        </button>
      </nav>
    </div>
  );
}
